import { useCallback, useEffect, useRef } from 'react'
import { Button } from '@w/ui/components/button'
import { GoogleIcon } from '@w/ui/components/icon'
import { useGoogleOneTapSignIn } from './google-one-tap'
import type { GoogleSignInButtonProps, UseGoogleSignInConfig, UseGoogleSignInResult } from './google-sign-in.types'
export type { GoogleSignInButtonProps, UseGoogleSignInConfig, UseGoogleSignInResult } from './google-sign-in.types'

export function useGoogleSignIn<AuthUser>({
  clientIds,
  onSuccess,
  onError,
  ...config
}: UseGoogleSignInConfig<AuthUser>): UseGoogleSignInResult {
  const callbacks = useRef({ onSuccess, onError })
  useEffect(() => {
    callbacks.current = { onSuccess, onError }
  }, [onSuccess, onError])
  // Stable callback identity keeps the existing GIS lifecycle initialized when
  // consumers supply inline callbacks. This is lifecycle correctness, not caching.
  const succeed = useCallback((user: AuthUser) => {
    callbacks.current.onSuccess?.(user)
  }, [])
  const fail = useCallback((error: Error) => {
    callbacks.current.onError?.(error)
  }, [])
  const google = useGoogleOneTapSignIn({
    ...config,
    clientId: clientIds.web,
    autoPrompt: false,
    onSuccess: succeed,
    onError: fail,
  })
  return {
    ready: google.isReady && config.enabled,
    error: google.error,
    signIn() {
      if (google.isReady && config.enabled) google.prompt()
      return Promise.resolve()
    },
  }
}

export function GoogleSignInButton({ disabled, label, className, signIn }: GoogleSignInButtonProps) {
  const parentRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const parent = parentRef.current
    if (!disabled && parent) {
      parent.replaceChildren()
      window.google?.accounts.id.renderButton(parent, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'pill',
        width: 400,
      })
      const frame = parent.querySelector('iframe')
      if (globalThis.crossOriginIsolated && frame) {
        // GIS inserts a non-COEP document. Reload it in an ephemeral context
        // so the app can keep isolation required by its database workers.
        const src = frame.src
        frame.setAttribute('credentialless', '')
        frame.src = src
      }
    }
    return () => { parent?.replaceChildren() }
  }, [disabled])
  return (
    <div className="group/google relative">
      <Button
        className={className}
        type="button"
        size="lg"
        variant="outlined"
        disabled={disabled}
        data-testid="login-google-button"
        onClick={() => { void signIn() }}
      >
        <GoogleIcon />
        {label}
      </Button>
      {!disabled && (
        <div
          ref={parentRef}
          className="absolute inset-0 z-10 flex cursor-pointer items-center justify-center overflow-hidden rounded-full opacity-0 [&>div]:scale-150"
          data-testid="login-google-provider-button"
        />
      )}
    </div>
  )
}
